import { apiClient } from './client'

export interface CatalogSegment {
  category: string
  count: number
}

export interface CatalogPart {
  artKey: string
  article: string // отображаемый артикул (as-is из УНФ / обученное написание)
  name: string | null // полное наименование из справочника УНФ
  brand: string | null // бренд из part_catalog (если известен)
  category: string | null
  // 'item' — парт заведён автоматически из позиции заявки, ещё без наименования.
  source: 'ref' | 'learned' | 'both' | 'item'
  confirmCount: number
  updatedAt: string
}

export interface CatalogPartsPage {
  items: CatalogPart[]
  nextOffset: number | null
  total: number
}

export interface CatalogSegmentsResponse {
  segments: CatalogSegment[]
  total: number // всего партномеров в каталоге
}

/** Сегменты с количеством партов + общий счётчик каталога. */
export async function getCatalogSegments(): Promise<CatalogSegmentsResponse> {
  const res = await apiClient.get<{ success: true; data: CatalogSegmentsResponse }>(
    '/catalog/segments',
  )
  return res.data.data
}

/** Все известные названия сегментов — для выпадашки при добавлении/смене. */
export async function getCatalogCategories(): Promise<string[]> {
  const res = await apiClient.get<{ success: true; data: string[] }>('/catalog/categories')
  return res.data.data
}

/** Партномера выбранного сегмента (страница, ?offset). */
export async function getCatalogParts(category: string, offset = 0): Promise<CatalogPartsPage> {
  const res = await apiClient.get<{ success: true; data: CatalogPartsPage }>('/catalog/parts', {
    params: { category, offset },
  })
  return res.data.data
}

/** Все партномера каталога — режим «Все сегменты» (страница, ?offset). */
export async function getCatalogAllParts(offset = 0): Promise<CatalogPartsPage> {
  const res = await apiClient.get<{ success: true; data: CatalogPartsPage }>('/catalog/all', {
    params: { offset },
  })
  return res.data.data
}

/** Глобальный поиск по артикулу / наименованию / сегменту. */
export async function searchCatalogParts(q: string): Promise<CatalogPart[]> {
  const res = await apiClient.get<{ success: true; data: CatalogPart[] }>('/catalog/search', {
    params: { q },
  })
  return res.data.data
}

/** Добавить парт в сегмент / переназначить сегмент по партномеру. */
export async function addCatalogPart(partNumber: string, category: string): Promise<CatalogPart> {
  const res = await apiClient.post<{ success: true; data: CatalogPart }>('/catalog/parts', {
    partNumber,
    category,
  })
  return res.data.data
}

/** Сменить сегмент у существующего парта (partNumber нужен для парта из УНФ). */
export async function changeCatalogSegment(
  artKey: string,
  category: string,
  partNumber?: string,
): Promise<CatalogPart> {
  const res = await apiClient.patch<{ success: true; data: CatalogPart }>('/catalog/parts', {
    artKey,
    category,
    ...(partNumber ? { partNumber } : {}),
  })
  return res.data.data
}

/** Убрать обученный оверрайд парта из каталога. */
export async function removeCatalogPart(artKey: string): Promise<void> {
  await apiClient.delete('/catalog/parts', { params: { artKey } })
}

// ── Карточка партномера ──

export interface PartCardIdentity {
  artKey: string
  article: string
  name: string | null
  category: string | null
  // 'item' — парт заведён автоматически из позиции заявки (без наименования).
  source: 'ref' | 'learned' | 'both' | 'item' | 'none'
  confirmCount: number
  brands: string[]
  // Бренд каталога (ручная правка приоритетнее) — редактируется в карточке
  // парта и подтягивается парсером в новые заявки.
  catalogBrand: string | null
  unfCode: string | null
  unit: string | null
  inCatalog: boolean
}

export interface PartCardRequestRow {
  itemId: string
  requestId: string
  requestNumber: string | null
  clientName: string | null
  managerName: string | null
  brand: string | null
  quantity: number
  status: string
  priority: string
  createdAt: string
}

export interface PartCardQuoteRow {
  id: string
  supplierName: string
  sourceType: string
  price: number | null
  currency: string
  priceWithCoeff: number | null
  note: string | null
  creatorName: string | null
  createdAt: string
}

export interface PartCardOrderRow {
  orderId: string
  requestNumber: string | null
  quantity: number
  price: number
  sum: number
  createdAt: string
}

export interface PartCard {
  identity: PartCardIdentity
  demand: {
    itemsCount: number
    requestsCount: number
    totalQuantity: number
    firstSeen: string | null
    lastSeen: string | null
    topClients: Array<{ name: string; count: number }>
    requests: PartCardRequestRow[]
  }
  procurement: {
    quotesCount: number
    byCurrency: Array<{ currency: string; minPrice: number; lastPrice: number }>
    quotes: PartCardQuoteRow[]
  }
  sales: {
    funnel: { requested: number; quoted: number; ordered: number }
    orderedQuantity: number
    orders: PartCardOrderRow[]
  }
}

/** Карточка партномера — вся информация по парту. */
export async function getPartCard(artKey: string): Promise<PartCard> {
  const res = await apiClient.get<{ success: true; data: PartCard }>('/catalog/part', {
    params: { artKey },
  })
  return res.data.data
}

/**
 * Правка карточки парта: бренд / полное наименование / сегмент. «Локально»:
 * существующие заявки не меняются, для новых заявок правка авторитетна.
 * name/brand: поле не передано → не трогается, пустая строка → сброс.
 */
export async function updatePartCard(payload: {
  artKey: string
  partNumber?: string
  category: string
  name?: string
  brand?: string
}): Promise<void> {
  await apiClient.patch('/catalog/part-card', payload)
}
